import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Delete,
  Edit,
  DriveEta,
  AssignmentInd,
  CheckCircle,
  Close,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";

function Driverallotment() {
  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [allotments, setAllotments] = useState([]);

  const [editId, setEditId] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [allotmentDate, setAllotmentDate] = useState(today);

  const [locations, setLocations] = useState([]);
  const [filterDate, setFilterDate] = useState(today);

  const schoolId = "zIlbJM5BjyrlEhg7fWKs";

  useEffect(() => {
    fetchData();
  }, []);

  const getVehicleLabel = (vehicle) =>
    vehicle?.vehicleNumber || vehicle?.vehicle_no || vehicle?.name || "";

  const getDriverLabel = (driver) =>
    driver?.name || driver?.firstname || driver?.fullName || "";

  const filteredAllotments = useMemo(() => {
    if (!filterDate) return allotments;
    return allotments.filter((item) => item.allotmentDate === filterDate);
  }, [allotments, filterDate]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [vehSnap, driSnap, allSnap, typeSnap, locSnap] = await Promise.all([
        getDocs(collection(db, "vehicles")),

        getDocs(
          query(
            collection(db, "users"),
            where("role", "==", "Driver"),
            where("schoolId", "==", schoolId)
          )
        ),

        getDocs(
          query(
            collection(db, "driver_allotments"),
            orderBy("createdAt", "desc")
          )
        ),

        getDocs(
          query(
            collection(db, "location_types"),
            where("schoolId", "==", schoolId),
            orderBy("fee", "asc")
          )
        ),

        getDocs(
          query(
            collection(db, "locations"),
            where("schoolId", "==", schoolId)
          )
        ),
      ]);

      setVehicles(vehSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setDrivers(driSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAllotments(allSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLocationTypes(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setSelectedVehicle(null);
    setSelectedDriver(null);
    setSelectedType("");
    setAllotmentDate(filterDate || today);
  };
  const locationMap = useMemo(() => {
    const map = {};

    locations.forEach((loc) => {
      if (!map[loc.locationType]) {
        map[loc.locationType] = [];
      }

      map[loc.locationType].push(loc);
    });

    return map;
  }, [locations]);

  const handleEdit = (item) => {
    const vehicle = vehicles.find((v) => v.id === item.vehicleId);
    const driver = drivers.find((d) => d.id === item.driverId);

    setEditId(item.id);
    setSelectedVehicle(vehicle || null);
    setSelectedDriver(driver || null);
    setSelectedType(item.locationType || "");
    setAllotmentDate(item.allotmentDate || today);
  };

  const handleSave = async () => {
    if (!selectedVehicle || !selectedDriver || !selectedType || !allotmentDate) {
      Swal.fire("Error", "Please fill all fields", "error");
      return;
    }

    try {
      const duplicateVehicle = allotments.some(
        (item) =>
          item.vehicleId === selectedVehicle.id &&
          item.allotmentDate === allotmentDate &&
          item.id !== editId
      );

      if (duplicateVehicle) {
        Swal.fire("Warning", "Vehicle already assigned for this date", "warning");
        return;
      }

      const payload = {
        vehicleId: selectedVehicle.id,
        vehicleNumber: getVehicleLabel(selectedVehicle),
        driverId: selectedDriver.id,
        driverName: getDriverLabel(selectedDriver),
        locationType: selectedType,
        allotmentDate,
        schoolId,
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "driver_allotments", editId), payload);
        Swal.fire("Updated", "Allotment updated successfully", "success");
      } else {
        const newRef = doc(collection(db, "driver_allotments"));

        await setDoc(newRef, {
          ...payload,
          status: "Active",
          createdAt: Timestamp.now(),
        });

        Swal.fire("Success", "Allotment created successfully", "success");
      }

      setFilterDate(allotmentDate);
      resetForm();
      fetchData();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Action failed", "error");
    }
  };

  const handleRemove = async (id) => {
    const result = await Swal.fire({
      title: "Delete Allotment?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "driver_allotments", id));
      Swal.fire("Deleted", "Allotment deleted successfully", "success");
      fetchData();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to delete allotment", "error");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={1} mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={1}>
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <AssignmentInd color="primary" />
        Driver & Route Allotment
      </Typography>

      <Grid className="row" spacing={2}>
        <Grid item xs={12} className="col-sm-4">
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2,
              borderTop: editId ? "4px solid #1976d2" : "1px solid #e0e0e0",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {editId ? "Edit Allotment" : "New Allotment"}
              </Typography>

              {editId && (
                <IconButton size="small" onClick={resetForm}>
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Stack>

            <Stack spacing={2}>
              <Autocomplete
                options={vehicles}
                value={selectedVehicle}
                getOptionLabel={getVehicleLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => setSelectedVehicle(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Vehicle" size="small" />
                )}
              />

              <Autocomplete
                options={drivers}
                value={selectedDriver}
                getOptionLabel={getDriverLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => setSelectedDriver(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Driver" size="small" />
                )}
              />

              <TextField
                select
                fullWidth
                label="Route Group"
                size="small"
                value={selectedType}
                onChange={(event) => setSelectedType(event.target.value)}
              >
                {locationTypes.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name} (₹{type.fee})
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                type="date"
                label="Allotment Date"
                size="small"
                value={allotmentDate}
                onChange={(event) => setAllotmentDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <Button
                variant="contained"
                startIcon={editId ? <Edit /> : <CheckCircle />}
                onClick={handleSave}
                color={editId ? "secondary" : "primary"}
                sx={{ py: 1 }}
              >
                {editId ? "Update Allotment" : "Confirm Allotment"}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} className="col-sm-8">
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Date Wise Allotments
              </Typography>

              <TextField
                type="date"
                label="Select Date"
                size="small"
                value={filterDate}
                onChange={(event) => setFilterDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: 70 }}>Sr No</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Vehicle</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Driver</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Route Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Locations</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredAllotments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No allotments found for selected date
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAllotments.map((item, index) => (
                      <TableRow key={item.id} hover selected={editId === item.id}>
                        <TableCell>{index + 1}</TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <DriveEta color="action" fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>
                              {item.vehicleNumber}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>{item.driverName}</TableCell>

                        <TableCell>
                          <Chip
                            label={item.locationType}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {(locationMap[item.locationType] || []).map((loc) => (
                              <Chip
                                key={loc.id}
                                label={loc.locationName}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {item.allotmentDate}
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleEdit(item)}
                            color="primary"
                            size="small"
                          >
                            <Edit fontSize="small" />
                          </IconButton>

                          <IconButton
                            onClick={() => handleRemove(item.id)}
                            color="error"
                            size="small"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Driverallotment;
